import { ArrowBack } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Button,
  ButtonVariants,
  ModalContentContainer,
  TextField,
} from '@/components';
import { Routes } from '@/constants';
import { Link } from '@/containers';
import { useForm } from '@/hooks';
import { AnalyticsService, EVENTS } from '@/services';
import { useLazyGetSSORedirectUriQuery, useLoginMutation } from '@/store';
import { Referrer } from '@/types';

import { OauthButton } from '../components';
import { LoginFormState } from './types';

type LoginFormProps = {
  onSignUpClick?: () => void;
  onLoginSuccess?: () => void;
  onModalClose?: () => void;
  referrer?: Referrer;
};

export const LoginForm = ({
  onSignUpClick,
  onLoginSuccess,
  onModalClose,
  referrer,
}: LoginFormProps) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { redirect } = Object.fromEntries(query.entries());

  const { form, setForm, errors, setErrors, validateForm, resetForm } =
    useForm<LoginFormState>({
      email: '',
      password: '',
    });
  const [failedSubmitCount, setFailedSubmitCount] = useState(0);
  const [isOnLoginStep, setIsOnLoginStep] = useState(false);

  const [trigger, { isLoading: isSSORedirectLoading }] =
    useLazyGetSSORedirectUriQuery();
  const [login, { isLoading }] = useLoginMutation();

  const onChange = ({
    target: { id, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prevState => ({
      ...prevState,
      [id]: value,
    }));
    setErrors(prevState => ({
      ...prevState,
      [id]: null,
    }));
  };

  const onContinueToLogin = async () => {
    const isValid = validateForm(['email']);
    if (!isValid) {
      return;
    }
    const { data: redirectUri } = await trigger({
      email: form.email.trim(),
      redirect,
    });
    if (redirectUri) {
      AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.SSO_REDIRECT, {
        method: 'workos',
      });
      window.location.href = redirectUri;
    } else {
      setIsOnLoginStep(true);
    }
  };

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    try {
      await login({
        email: form.email.trim(),
        password: form.password.trim(),
        referrer,
      }).unwrap();
    } catch (error) {
      const requestError = error as FetchBaseQueryError;
      setFailedSubmitCount(prevState => prevState + 1);
      setErrors({
        email:
          failedSubmitCount >= 2
            ? 'Try entering a different email, like your company or personal email.'
            : undefined,
        password:
          (requestError?.status === 400 &&
            (requestError?.data as { message: string })?.message) ||
          'Something went wrong. Please try again later.',
      });
      AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.LOGIN.FAIL, {
        reason: (requestError?.data as { message: string })?.message || '',
      });
      return;
    }

    setFailedSubmitCount(0);
    onLoginSuccess?.();
  };

  return (
    <ModalContentContainer onModalClose={onModalClose}>
      <Typography variant="h1">Log in</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        New user?{' '}
        <Link
          textVariant="body2"
          to={
            onSignUpClick ? undefined : `${Routes.REGISTER}${location.search}`
          }
          onClick={onSignUpClick}>
          Create an account
        </Link>
      </Typography>
      <TextField
        id="email"
        fullWidth
        placeholder="Enter your email"
        value={form.email}
        label="Email"
        onChange={onChange}
        error={!!errors.email}
        helperText={errors.email}
        sx={{ mt: 6 }}
        disabled={isOnLoginStep}
        onEnterPress={isOnLoginStep ? onSubmit : onContinueToLogin}
      />
      {!isOnLoginStep && (
        <>
          <Button
            fullWidth
            label="Continue"
            disabled={isSSORedirectLoading}
            skinny={false}
            sx={{ mt: 2 }}
            onClick={onContinueToLogin}
          />
          <Box sx={{ mt: 2 }}>
            <Link to={Routes.FORGOT_PASSWORD}>Forgot Password</Link>
          </Box>
          <OauthButton type="google" sx={{ mt: 6 }} />
          <OauthButton type="apple" sx={{ mt: 1.5 }} />
        </>
      )}
      {isOnLoginStep && (
        <>
          <TextField
            id="password"
            value={form.password}
            fullWidth
            label="Password"
            type="password"
            placeholder="Password"
            sx={{ mt: 2 }}
            error={!!errors.password}
            helperText={errors.password}
            onChange={onChange}
            onEnterPress={onSubmit}
          />
          <Box sx={{ mt: 2 }}>
            <Link to={Routes.FORGOT_PASSWORD}>Forgot Password</Link>
          </Box>
          <Button
            fullWidth
            disabled={isLoading}
            label="Log In"
            sx={{ mt: 2 }}
            onClick={onSubmit}
            skinny={false}
          />
          <Box sx={{ alignSelf: 'flex-start' }}>
            <Button
              skinny={false}
              fullWidth={false}
              variant={ButtonVariants.PLAIN}
              label="Go back"
              onClick={() => {
                setIsOnLoginStep(false);
                resetForm();
              }}
              startIcon={<ArrowBack />}
            />
          </Box>
        </>
      )}
    </ModalContentContainer>
  );
};
